import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TomSelect from "tom-select";
import {
  ArrowLeft,
  Plus,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  ShoppingCart,
  Minus,
  X,
  RefreshCw,
  Package,
  MapPin,
  FileText,
  ListOrdered,
  Store,
  Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { getAllAssets, saveAllAssets } from "./db/assetDB";

const API_BASE = "https://jne-cilacap.com/slip/public/api";
const LS_ORDERS_KEY = "atk_orders_history";
const CACHE_KEY = "cached_atk_specific_items";
const ALLOWED_LOCATION_PREFIXES = [
  "depan",
  "ruang",
  "kp",
  "agen",
  "counter",
  "cash",
  "outbound",
  "inbound",
  "delivery",
  "undel",
  "pickup",
];

const filterAllowedLocations = (items = []) => {
  const filtered = items.filter((item) => {
    const name = String(item?.nama || item?.name || "").toLowerCase().trim();
    return ALLOWED_LOCATION_PREFIXES.some((prefix) => name.startsWith(prefix));
  });

  return filtered.length ? filtered : items;
};

// ── TomSelect wrapper (sama seperti di menu Asset: Form Pilih Lokasi Baru) ───────
const TomSelectField = ({ selectRef, options, value, onChange, placeholder }) => {
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!selectRef.current || options.length === 0) return;

    if (instanceRef.current) {
      instanceRef.current.clearOptions();
      instanceRef.current.addOptions(options);
      instanceRef.current.refreshOptions(false);
      if (value) instanceRef.current.setValue(value, true);
      return;
    }

    instanceRef.current = new TomSelect(selectRef.current, {
      valueField: "id",
      labelField: "nama",
      searchField: "nama",
      placeholder: placeholder || "Cari dan pilih...",
      options,
      render: {
        option: (d, esc) => `
          <div style="padding:8px 12px;border-bottom:1px solid #f1f5f9">
            <div style="font-weight:600;color:#1e293b;font-size:13px">${esc(d.nama)}</div>
          </div>`,
        item: (d, esc) => `<div style="font-weight:500;font-size:13px">${esc(d.nama)}</div>`,
      },
      onChange: (v) => onChange(v),
    });

    if (value) {
      instanceRef.current.setValue(value, true);
    }

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [options]);

  useEffect(() => {
    if (instanceRef.current && value !== undefined) {
      if (instanceRef.current.getValue() !== String(value)) {
        instanceRef.current.setValue(value || "", true);
      }
    }
  }, [value]);

  return null;
};

/* ─── Status Helper for Deletion ─── */
const isDeletableStatus = (status = "") => {
  const s = status.toLowerCase().trim();
  if (s.includes("siap") || s.includes("kirim") || s.includes("erima") || s.includes("terima") || s.includes("proses")) return false;
  return ["baru", "pending", "order", "belum disiapkan", "menunggu"].includes(s);
};

/* ─── Default catalog fallback if API is unreachable ─── */
const DEFAULT_CATALOG = [
  { id: "atk-1", kode: "ATK-HVS-A4", nama: "Kertas HVS A4 80gr PaperOne", kategori: "atk", satuan: "Rim", lokasi: "Gudang ATK", untuk: "semua" },
  { id: "atk-2", kode: "ATK-HVS-F4", nama: "Kertas HVS F4 75gr Sinar Dunia", kategori: "atk", satuan: "Rim", lokasi: "Gudang ATK", untuk: "semua" },
  { id: "atk-3", kode: "ATK-TINTA-BK", nama: "Tinta Printer Canon GI-790 Black", kategori: "atk", satuan: "Botol", lokasi: "Gudang ATK", untuk: "semua" },
  { id: "atk-4", kode: "ATK-PEN-G2", nama: "Ballpoint Pilot G2 0.5 Black", kategori: "atk", satuan: "Lusin", lokasi: "Gudang ATK", untuk: "semua" },
  { id: "atk-5", kode: "ATK-STAPLER", nama: "Stapler Max HD-10 Original", kategori: "atk", satuan: "Pcs", lokasi: "Gudang ATK", untuk: "semua" },
];


const INIT_ORDERS = [
  {
    id: "ATK-20241101", no_order: "ATK-20241101",
    items: [{ id: "1", nama: "Kertas HVS A4 80gr", qty: 5, satuan: "Rim", kode: "ATK-001" }],
    keperluan: "Operasional Kantor Cabang", pemohon: "Budi Santoso",
    lokasi: "Kantor Cabang Cilacap", status: "approved", tanggal: "2024-11-01",
  },
  {
    id: "ATK-20241110", no_order: "ATK-20241110",
    items: [
      { id: "2", nama: "Tinta Printer Canon Black", qty: 2, satuan: "Botol", kode: "ATK-002" },
      { id: "3", nama: "Ballpoint Pilot G2 0.5", qty: 12, satuan: "Pcs", kode: "ATK-003" },
    ],
    keperluan: "Isi Ulang Printer & Frontdesk", pemohon: "Sari Dewi",
    lokasi: "Agen Adipala", status: "pending", tanggal: "2024-11-10",
  },
];

const STATUS_CFG = {
  // ─── baru / pending → Ngantri / Belum Disiapkan ────────────────────────
  baru:        { label: "Ngantri / Belum Disiapkan", color: "#d97706", bg: "#fffbeb", icon: <Clock size={12} /> },
  pending:     { label: "Ngantri / Belum Disiapkan", color: "#d97706", bg: "#fffbeb", icon: <Clock size={12} /> },
  order:       { label: "Ngantri / Belum Disiapkan", color: "#d97706", bg: "#fffbeb", icon: <Clock size={12} /> },
  menunggu:    { label: "Ngantri / Belum Disiapkan", color: "#d97706", bg: "#fffbeb", icon: <Clock size={12} /> },
  // ─── proses → Sedang Disiapkan ──────────────────────────────────────────
  proses:      { label: "Sedang Disiapkan",           color: "#2563eb", bg: "#eff6ff", icon: <Clock size={12} /> },
  diproses:    { label: "Sedang Disiapkan",           color: "#2563eb", bg: "#eff6ff", icon: <Clock size={12} /> },
  // ─── disiapkan / diisiapkan → Sudah Disiapkan ───────────────────────────
  diisiapkan:  { label: "Sudah Disiapkan",            color: "#059669", bg: "#ecfdf5", icon: <CheckCircle size={12} /> },
  disiapkan:   { label: "Sudah Disiapkan",            color: "#059669", bg: "#ecfdf5", icon: <CheckCircle size={12} /> },
  approved:    { label: "Sudah Disiapkan",            color: "#059669", bg: "#ecfdf5", icon: <CheckCircle size={12} /> },
  selesai:     { label: "Sudah Disiapkan",            color: "#059669", bg: "#ecfdf5", icon: <CheckCircle size={12} /> },
  siap:        { label: "Sudah Disiapkan",            color: "#059669", bg: "#ecfdf5", icon: <CheckCircle size={12} /> },
  "sudah disiapkan": { label: "Sudah Disiapkan",      color: "#059669", bg: "#ecfdf5", icon: <CheckCircle size={12} /> },
  // ─── dikirim → Sudah Dikirim ─────────────────────────────────────────────
  dikirim:     { label: "Sudah Dikirim",              color: "#7c3aed", bg: "#f5f3ff", icon: <Package size={12} /> },
  kirim:       { label: "Sudah Dikirim",              color: "#7c3aed", bg: "#f5f3ff", icon: <Package size={12} /> },
  "sudah dikirim": { label: "Sudah Dikirim",          color: "#7c3aed", bg: "#f5f3ff", icon: <Package size={12} /> },
  // ─── diterima / erima → Sudah Diterima ───────────────────────────────────
  diterima:    { label: "Sudah Diterima",             color: "#0891b2", bg: "#ecfeff", icon: <CheckCircle size={12} /> },
  terima:      { label: "Sudah Diterima",             color: "#0891b2", bg: "#ecfeff", icon: <CheckCircle size={12} /> },
  erima:       { label: "Sudah Diterima",             color: "#0891b2", bg: "#ecfeff", icon: <CheckCircle size={12} /> },
  "sudah diterima": { label: "Sudah Diterima",        color: "#0891b2", bg: "#ecfeff", icon: <CheckCircle size={12} /> },
  // ─── rejected / ditolak ──────────────────────────────────────────────────
  rejected:    { label: "Ditolak",                    color: "#dc2626", bg: "#fef2f2", icon: <XCircle size={12} /> },
  ditolak:     { label: "Ditolak",                    color: "#dc2626", bg: "#fef2f2", icon: <XCircle size={12} /> },
};

const getCfg = (raw) => {
  const s = String(raw || "").toLowerCase().trim();
  if (STATUS_CFG[s]) return STATUS_CFG[s];
  if (s.includes("erima") || s.includes("terima")) return STATUS_CFG.diterima;
  if (s.includes("kirim")) return STATUS_CFG.dikirim;
  if (s.includes("siap")) return STATUS_CFG.diisiapkan;
  if (s.includes("proses")) return STATUS_CFG.proses;
  if (s.includes("tolak") || s.includes("reject") || s.includes("batal")) return STATUS_CFG.ditolak;
  return STATUS_CFG.baru;
};

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d || "-"; }
};

const itemIcon = (kat = "", nama = "") => {
  const t = `${kat} ${nama}`.toLowerCase();
  if (t.includes("kertas") || t.includes("hvs")) return "📄";
  if (t.includes("tinta") || t.includes("toner")) return "🖨️";
  if (t.includes("pen") || t.includes("spidol")) return "🖊️";
  if (t.includes("stapler") || t.includes("gunting") || t.includes("lakban")) return "✂️";
  if (t.includes("map") || t.includes("arsip")) return "📁";
  return "📦";
};

/* ═══════════════════════════════════════════════════════════ */
export default function ATKPemesananPage() {
  const navigate = useNavigate();

  /* ─── VIEW: "history" | "catalog" ─── */
  const [view, setView] = useState("history");

  /* ─── Data ─── */
  const [assets, setAssets] = useState(DEFAULT_CATALOG);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(() => {
    try {
      const s = localStorage.getItem(LS_ORDERS_KEY);
      return s ? JSON.parse(s) : INIT_ORDERS;
    } catch { return INIT_ORDERS; }
  });

  /* ─── Cart ─── */
  const [cart, setCart] = useState({});

  /* ─── Modals ─── */
  const [showCheckout, setShowCheckout]     = useState(false);
  const [selectedOrder, setSelectedOrder]   = useState(null);

  /* ─── Filters ─── */
  const [search, setSearch]               = useState("");
  const [catFilter, setCatFilter]         = useState("Semua");
  const [histSearch, setHistSearch]       = useState("");

  /* ─── Checkout form ─── */
  const [instruksi, setInstruksi]   = useState("");
  const [lokasiTujuan, setLokasi]   = useState("");
  const [lokasiId, setLokasiId]     = useState("");
  const [locations, setLocations]   = useState([]);
  const [loadingLoc, setLoadingLoc] = useState(false);
  const selectLocationRef           = useRef(null);

  /* ─── Current user (stable) ─── */
  const currentUser = useMemo(() => {
    try {
      const k = JSON.parse(localStorage.getItem("data_karyawan") || "{}");
      const u = JSON.parse(localStorage.getItem("data_user") || "{}");
      const isAgen = !!k.is_agen || !!u.is_agen || 
        String(k.role || "").toLowerCase() === "agen" || 
        String(u.role || "").toLowerCase() === "agen" || 
        localStorage.getItem("is_agen") === "true" ||
        (Boolean(k.nik) && !String(k.nik).toUpperCase().startsWith("CXP"));

      const nama = [k.nama_depan, k.nama_belakang].filter(Boolean).join(" ") || k.nama || k.name || "Karyawan";
      const no_hp = k.no_hp || k.no_wa || k.telepon || k.hp || k.phone || "";
      const kantor = k.kantor || "";
      const kantor_id = k.kantor_id || k.id_kantor || k.lokasi_id || null;
      // Nama agen (untuk matching lokasi tujuan default)
      const agenNama = k.agen || k.nama_agen || u.agen || u.nama_agen || kantor || "";
      return { nama, kantor, id: k.id || null, no_hp, kantor_id, isAgen, agenNama };
    } catch { return { nama: "Karyawan", kantor: "", id: null, no_hp: "", kantor_id: null, isAgen: false, agenNama: "" }; }
  }, []);

  /* ─── Role / Gender Access Guard: Jangan berikan akses jika data_user gender = female ─── */
  const isFemaleUser = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("data_user") || "{}");
      const k = JSON.parse(localStorage.getItem("data_karyawan") || "{}");
      const g = (u.gender || u.jenis_kelamin || k.gender || k.jenis_kelamin || "").toString().trim().toLowerCase();
      return g === "female" || g === "perempuan" || g === "wanita" || g === "p";
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (isFemaleUser) {
      Swal.fire({
        icon: "warning",
        title: "Akses Dibatasi",
        text: "Menu pemesanan ATK tidak tersedia untuk akun Anda.",
        confirmButtonText: "Kembali ke Beranda",
        customClass: { confirmButton: "btn btn-primary" },
      }).then(() => {
        navigate("/", { replace: true });
      });
    }
  }, [isFemaleUser, navigate]);

  /* ─── Persist orders ─── */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_ORDERS_KEY, JSON.stringify(orders)); } catch {}
  }, [orders]);

  /* ─── Fetch Locations from /asset-locations ─── */
  useEffect(() => {
    if (isFemaleUser) return;
    const fetchLocations = async () => {
      setLoadingLoc(true);
      try {
        const res = await fetch(`${API_BASE}/asset-locations`);
        const json = await res.json();
        if (json.status === "success" && Array.isArray(json.data)) {
          const mapped = json.data.map((l) => ({
            id: l.id,
            nama: l.name,
          }));
          const allowed = filterAllowedLocations(mapped);
          setLocations(allowed);

          // Untuk agen: cari lokasi yang cocok dengan nama agen (misal "AGEN RINJANI" → "Agen Rinjani")
          // Untuk non-agen: cari lokasi yang cocok dengan kantor karyawan
          const searchKey = currentUser.isAgen
            ? (currentUser.agenNama || currentUser.kantor || "")
            : (currentUser.kantor || "");
          const searchKeyLow = searchKey.toLowerCase().trim();

          // Ekstrak kata kunci utama dari nama agen (singkirkan kata umum seperti "agen", "jne")
          const agenKeywords = searchKeyLow
            .split(/\s+/)
            .filter(w => w.length > 2 && !['agen', 'counter', 'jne', 'kantor', 'cabang'].includes(w));

          const matched =
            // 1. Exact match
            allowed.find(l => l.nama.toLowerCase().trim() === searchKeyLow) ||
            // 2. Lokasi mengandung semua keyword nama agen
            (agenKeywords.length > 0 && allowed.find(l => {
              const lNama = l.nama.toLowerCase();
              return agenKeywords.every(kw => lNama.includes(kw));
            })) ||
            // 3. Salah satu kata kunci paling panjang ada di nama lokasi
            (agenKeywords.length > 0 && allowed.find(l => {
              const lNama = l.nama.toLowerCase();
              const longestKw = [...agenKeywords].sort((a, b) => b.length - a.length)[0];
              return lNama.includes(longestKw);
            })) ||
            // 4. Fallback: searchKey mengandung nama lokasi
            allowed.find(l => searchKeyLow.includes(l.nama.toLowerCase()));

          if (matched) {
            setLokasi(matched.nama);
            setLokasiId(String(matched.id));
            console.log(`📍 [ATK] Default lokasi tujuan (${currentUser.isAgen ? 'agen' : 'kantor'}): "${matched.nama}" (id: ${matched.id})`);
          } else if (allowed.length > 0) {
            // Jika tidak ada yang cocok, tetap kosong agar agen memilih sendiri
            if (!currentUser.isAgen) {
              setLokasi(searchKey || allowed[0].nama);
              setLokasiId(String(allowed[0].id));
            }
          }
        }
      } catch (e) {
        console.warn("⚠️ Gagal mengambil daftar lokasi:", e);
      } finally {
        setLoadingLoc(false);
      }
    };

    fetchLocations();
  }, [currentUser.kantor, currentUser.isAgen, currentUser.agenNama]);

  /* ─── Fetch Orders from Backend API ─── */
  const loadOrders = useCallback(async () => {
    try {
      const url = currentUser.id 
        ? `${API_BASE}/atk/orders?user_created=${currentUser.id}`
        : `${API_BASE}/atk/orders`;
      
      console.log("🔄 [ATK] Fetching orders from:", url);
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        if (json.status === "success" && Array.isArray(json.data)) {
          console.log("✅ [ATK] Orders loaded from server:", json.data.length, "items");
          const mapped = json.data.map((o) => ({
            id: o.id,
            raw_id: o.id,
            no_order: o.no_nota || `ATK-${o.id}`,
            items: (o.details || []).map((d) => ({
              id: d.id_barang,
              nama: d.barang?.nama || d.nama_barang || `Barang #${d.id_barang}`,
              qty: d.jumlah,
              satuan: d.barang?.satuan || "Pcs",
              kode: d.barang?.kode || "-",
              harga: d.harga || 0,
            })),
            instruksi: o.keterangan || "-",
            keperluan: o.keterangan || "-",
            pemohon: o.agen || currentUser.nama,
            lokasi: o.lokasi || currentUser.kantor || "Kantor",
            status: o.status || "baru",
            petugas: o.petugas || null,
            shift: o.shift || null,
            jam: o.jam || null,
            catatan: o.catatan || null,
            catatan_kirim: o.catatan_kirim || null,
            tanggal: o.tanggal || o.created_at,
            created_at: o.created_at,
          }));
          if (mapped.length > 0) {
            setOrders(mapped);
          }
        }
      }
    } catch (err) {
      console.warn("⚠️ [ATK] Gagal fetch orders dari server:", err);
    }
  }, [currentUser]);

  /* ─── Load ATK items directly from /atk endpoint ─── */
  const loadAssets = useCallback(async (force = false) => {
    setLoading(true);
    const activeCacheKey = currentUser.isAgen ? `${CACHE_KEY}_agen` : CACHE_KEY;
    try {
      // 1. Cek LocalStorage cache khusus ATK
      if (!force) {
        const cached = localStorage.getItem(activeCacheKey);
        if (cached) {
          try {
            const p = JSON.parse(cached);
            if (Array.isArray(p) && p.length > 0) {
              setAssets(p);
              setLoading(false);
            }
          } catch {}
        }
      }

      // 2. Fetch langsung dari endpoint /atk backend (sertakan ?untuk=semua jika akun Agen)
      const targetUrl = currentUser.isAgen 
        ? `${API_BASE}/atk?untuk=semua` 
        : `${API_BASE}/atk`;

      console.log("🔄 [ATK] Fetching catalog from:", targetUrl);
      const res = await fetch(targetUrl);
      if (res.ok) {
        const json = await res.json();
        if (json.status === "success" && Array.isArray(json.data) && json.data.length > 0) {
          console.log("✅ [ATK] Catalog loaded from server:", json.data.length, "items");
          setAssets(json.data);
          localStorage.setItem(activeCacheKey, JSON.stringify(json.data));
          setLoading(false);
          return;
        }
      }

      // 3. Fallback jika /atk belum return data
      const fallback = localStorage.getItem(activeCacheKey);
      if (!fallback) {
        setAssets(DEFAULT_CATALOG);
      }
    } catch (err) {
      console.warn("⚠️ [ATK] Gagal load dari /api/atk:", err);
      const fallback = localStorage.getItem(activeCacheKey);
      if (fallback) {
        try { setAssets(JSON.parse(fallback)); } catch { setAssets(DEFAULT_CATALOG); }
      } else {
        setAssets(DEFAULT_CATALOG);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser.isAgen]);

  useEffect(() => {
    if (isFemaleUser) return;
    loadAssets(true); // Selalu fetch data /atk terbaru saat pertama buka
    loadOrders();     // Fetch riwayat pesanan dari server
    if (currentUser.kantor) setLokasi(currentUser.kantor);
  }, [loadAssets, loadOrders, currentUser.kantor, isFemaleUser]);

  /* ─── Base Catalog: Filter khusus agen (hanya kolom untuk = 'semua') ─── */
  const baseAssets = useMemo(() => {
    if (!currentUser.isAgen) return assets;
    return assets.filter((a) => {
      const val = String(a.untuk || "").toLowerCase().trim();
      if (!val) return false;
      if (val === "semua") return true;
      const tokens = val.split(/[,;/|]+/).map((t) => t.trim());
      return tokens.includes("semua");
    });
  }, [assets, currentUser.isAgen]);


  /* ─── Derived ─── */
  const categories = useMemo(() => {
    const set = new Set();
    baseAssets.forEach((a) => {
      if (a.subkategori && a.subkategori.trim()) set.add(a.subkategori.trim());
      else if (a.kategori && a.kategori.trim()) set.add(a.kategori.trim());
    });
    return ["Semua", ...Array.from(set).slice(0, 10)];
  }, [baseAssets]);

  const filteredAssets = useMemo(() =>
    baseAssets.filter((a) => {
      const q = search.toLowerCase();
      const matchQ = !q || [a.nama, a.kode, a.kategori, a.subkategori, a.keterangan, a.lokasi].some((v) => v?.toLowerCase().includes(q));
      const matchCat = catFilter === "Semua" || 
        a.subkategori?.toLowerCase() === catFilter.toLowerCase() || 
        a.kategori?.toLowerCase() === catFilter.toLowerCase();
      return matchQ && matchCat;
    }), [baseAssets, search, catFilter]);


  const filteredOrders = useMemo(() =>
    orders.filter((o) => {
      const q = histSearch.toLowerCase();
      const items = (o.items || []).map((i) => i.nama).join(" ").toLowerCase();
      return !q || [o.no_order, o.pemohon, o.instruksi, o.keperluan].some((v) => v?.toLowerCase().includes(q)) || items.includes(q);
    }), [orders, histSearch]);

  const totalQty = useMemo(() => Object.values(cart).reduce((s, i) => s + i.qty, 0), [cart]);
  const cartList  = useMemo(() => Object.entries(cart).map(([k, v]) => ({ key: k, ...v })), [cart]);

  /* ─── Cart ops ─── */
  const addToCart = (asset) => {
    const key = String(asset.id || asset.kode || Math.random());
    setCart((prev) => ({
      ...prev,
      [key]: { asset, qty: (prev[key]?.qty || 0) + 1, satuan: asset.satuan || "Pcs" },
    }));
  };

  const updateQty = (key, delta) => {
    setCart((prev) => {
      const item = prev[key];
      if (!item) return prev;
      const newQty = item.qty + delta;
      if (newQty <= 0) { const c = { ...prev }; delete c[key]; return c; }
      return { ...prev, [key]: { ...item, qty: newQty } };
    });
  };

  const setDirectQty = (key, rawQty, asset) => {
    const val = parseInt(rawQty, 10);
    setCart((prev) => {
      const copy = { ...prev };
      if (isNaN(val) || val <= 0) {
        delete copy[key];
        return copy;
      }
      const existing = prev[key] || { asset, satuan: asset?.satuan || "Pcs" };
      return {
        ...prev,
        [key]: { ...existing, qty: val },
      };
    });
  };

  const clearCart = () => setCart({});

  /* ─── Checkout ke API Backend ─── */
  const handleCheckout = async () => {
    if (cartList.length === 0) {
      return Swal.fire({ icon: "warning", title: "Keranjang kosong", text: "Pilih minimal 1 item ATK." });
    }
    if (!instruksi.trim()) {
      return Swal.fire({ icon: "warning", title: "Instruksi Wajib Diisi", text: "Tuliskan instruksi / catatan pemesanan ATK ini." });
    }

    if (!lokasiId) {
      return Swal.fire({
        icon: "warning",
        title: "Lokasi Belum Dipilih",
        text: "Silakan pilih lokasi tujuan pengiriman ATK terlebih dahulu.",
      });
    }

    const ok = await Swal.fire({
      title: "Konfirmasi Pesanan",
      text: `Kirim ${cartList.length} jenis ATK (${totalQty} total item) untuk ${currentUser.nama} ke ${lokasiTujuan || "Lokasi Terpilih"}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Kirim",
      cancelButtonText: "Batal",
      confirmButtonColor: "#8b5cf6",
    });
    if (!ok.isConfirmed) return;

    const now = new Date();
    const tanggalStr = now.toISOString().split("T")[0];

    // 👉 user_created diisi ID LOKASI TUJUAN yang dipilih dari TomSelect
    const payload = {
      user_created: lokasiId, // ID Lokasi Tujuan
      id_lokasi: lokasiId,
      tanggal: tanggalStr,
      keterangan: instruksi.trim(),
      wa_pelapor: currentUser.no_hp || "",
      agen: currentUser.nama,
      connote_agen: currentUser.nama,
      lokasi: lokasiTujuan || "Kantor",
      items: cartList.map((c) => ({
        id_barang: c.asset.id || c.key,
        jumlah: c.qty,
        harga: c.asset.harga || c.asset.harga_jual || 0,
        nama_barang: c.asset.nama || "Item",
        satuan: c.satuan || "Pcs",
      })),
    };

    console.log("📤 [ATK ORDER] Mengirim payload ke Backend (user_created = ID Lokasi):", payload);

    Swal.fire({
      title: "Mengirim Pesanan...",
      text: "Sedang memproses dan menyimpan ke database...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await fetch(`${API_BASE}/atk/order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const resData = await response.json();
      console.log("📥 [ATK ORDER] Respon dari server:", response.status, resData);

      if (response.ok && (resData.status === "success" || resData.data)) {
        const orderData = resData.data || {};
        const noOrder = orderData.no_nota || orderData.id || `ATK-${Date.now()}`;

        // Update local state orders
        const newLocalOrder = {
          id: noOrder,
          no_order: noOrder,
          items: cartList.map((c) => ({
            id: c.key,
            kode: c.asset.kode || "-",
            nama: c.asset.nama || "Item",
            qty: c.qty,
            satuan: c.satuan,
          })),
          instruksi: instruksi.trim(),
          keperluan: instruksi.trim(),
          pemohon: currentUser.nama,
          lokasi: lokasiTujuan || currentUser.kantor || "Kantor Pusat",
          status: "baru",
          tanggal: tanggalStr,
          created_at: now.toISOString(),
        };

        setOrders((p) => [newLocalOrder, ...p]);
        clearCart();
        setInstruksi("");
        setShowCheckout(false);
        setView("history");

        // Reload data terbaru dari server
        loadAssets(true);
        loadOrders();

        await Swal.fire({
          icon: "success",
          title: "Berhasil!",
          text: `Pesanan ATK #${noOrder} berhasil tercatat di database!`,
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        throw new Error(resData.message || "Gagal menyimpan ke database");
      }
    } catch (error) {
      console.error("❌ [ATK ORDER] Gagal mengirim pesanan:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal Mengirim Pesanan",
        text: error.message || "Terjadi kesalahan saat menghubungkan ke server.",
      });
    }
  };

  /* ─── Hapus / Batalkan Pesanan (jika status baru/pending/belum disiapkan) ─── */
  const handleDeleteOrder = async (order, e) => {
    if (e) e.stopPropagation();

    if (!isDeletableStatus(order.status)) {
      return Swal.fire({
        icon: "warning",
        title: "Tidak Dapat Dihapus",
        text: `Pesanan dengan status "${order.status}" tidak dapat dibatalkan/dihapus karena sudah diproses.`,
      });
    }

    const confirm = await Swal.fire({
      title: "Hapus Pesanan ATK?",
      html: `Apakah Anda yakin ingin membatalkan & menghapus pesanan <b>${order.no_order || order.id}</b>?<br><br><small style="color:#6b7280">Stok barang yang sudah dipesan akan otomatis dikembalikan ke gudang.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
    });

    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Menghapus...",
      text: "Sedang membatalkan pesanan dan mengembalikan stok...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const orderId = order.raw_id || order.id;
      // Gunakan POST /api/atk/order/delete/{id} untuk menghindari blokir CORS/method DELETE pada server hosting
      const res = await fetch(`${API_BASE}/atk/order/delete/${orderId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ id: orderId }),
      });

      const resData = await res.json();
      console.log("🗑️ [ATK DELETE RESPONSE]:", res.status, resData);

      if (res.ok && resData.status === "success") {
        // Hapus dari state lokal
        setOrders((prev) => prev.filter((o) => o.id !== order.id && o.no_order !== order.no_order));
        if (selectedOrder && (selectedOrder.id === order.id || selectedOrder.no_order === order.no_order)) {
          setSelectedOrder(null);
        }

        // Muat ulang katalog (stok kembali) & daftar pesanan
        loadAssets(true);
        loadOrders();

        await Swal.fire({
          icon: "success",
          title: "Berhasil Dihapus!",
          text: resData.message || "Pesanan berhasil dibatalkan dan stok telah dikembalikan.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        throw new Error(resData.message || "Gagal menghapus pesanan di server.");
      }
    } catch (err) {
      console.error("❌ [ATK DELETE ERROR]:", err);
      // Tetap beri opsi hapus dari local jika ID lokal belum pernah sync
      Swal.fire({
        icon: "error",
        title: "Gagal Menghapus",
        text: err.message || "Terjadi kesalahan saat membatalkan pesanan.",
      });
    }
  };

  /* ─── Konfirmasi Penerimaan Pesanan oleh Agen (status dikirim -> diterima) ─── */
  const handleConfirmReceived = async (order, e) => {
    if (e) e.stopPropagation();

    const orderId = order.raw_id || order.id;
    const orderNo = order.no_order || order.id;

    const confirm = await Swal.fire({
      title: "Konfirmasi Penerimaan?",
      html: `Apakah pesanan ATK <b>${orderNo}</b> sudah Anda terima dengan baik dan lengkap?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Ya, Sudah Diterima",
      cancelButtonText: "Batal",
    });

    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Memproses...",
      text: "Memperbarui status pesanan menjadi Sudah Diterima...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // 1. Panggil endpoint backend jika online
      const endpoints = [
        `${API_BASE}/atk/order/terima/${orderId}`,
        `${API_BASE}/atk/order/status/${orderId}`,
      ];

      for (const ep of endpoints) {
        try {
          const res = await fetch(ep, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
            },
            body: JSON.stringify({
              id: orderId,
              status: "diterima",
              user_id: currentUser.id,
              catatan_terima: `Diterima oleh ${currentUser.nama}`,
            }),
          });
          if (res.ok) break;
        } catch {}
      }

      // 2. Update state lokal
      const updatedStatus = "diterima";
      setOrders((prev) =>
        prev.map((o) =>
          (o.id === order.id || o.no_order === order.no_order)
            ? { ...o, status: updatedStatus }
            : o
        )
      );

      setSelectedOrder((prev) =>
        prev && (prev.id === order.id || prev.no_order === order.no_order)
          ? { ...prev, status: updatedStatus }
          : prev
      );

      // 3. Update localStorage cache
      try {
        const cached = JSON.parse(localStorage.getItem(LS_ORDERS_KEY) || "[]");
        const updatedCache = cached.map((o) =>
          (o.id === order.id || o.no_order === order.no_order)
            ? { ...o, status: updatedStatus }
            : o
        );
        localStorage.setItem(LS_ORDERS_KEY, JSON.stringify(updatedCache));
      } catch {}

      // Refresh data dari server
      loadOrders();

      await Swal.fire({
        icon: "success",
        title: "Pesanan Diterima!",
        text: `Pesanan ${orderNo} berhasil dikonfirmasi telah diterima.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.warn("⚠️ Konfirmasi offline fallback:", err);
      setOrders((prev) =>
        prev.map((o) =>
          (o.id === order.id || o.no_order === order.no_order)
            ? { ...o, status: "diterima" }
            : o
        )
      );
      setSelectedOrder((prev) =>
        prev && (prev.id === order.id || prev.no_order === order.no_order)
          ? { ...prev, status: "diterima" }
          : prev
      );
      Swal.fire({
        icon: "success",
        title: "Pesanan Diterima",
        text: "Status telah diperbarui di perangkat Anda.",
      });
    }
  };

  /* ═══════════ STYLES ═══════════ */
  const s = {
    page: { minHeight: "100vh", background: "linear-gradient(180deg,#F5F3FF 0%,#EDE9FE 40%,#fff 100%)", fontFamily: "'Inter','Poppins',sans-serif", paddingBottom: 110 },
    header: { padding: "16px 16px 0", display: "flex", alignItems: "center", gap: 12 },
    backBtn: { width: 40, height: 40, borderRadius: "50%", background: "#fff", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, boxShadow: "0 2px 6px rgba(0,0,0,0.08)" },
    title: { flex: 1, color: "#111827", fontSize: 18, fontWeight: 700, margin: 0 },
    cartBtn: { display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", boxShadow: "0 4px 14px rgba(16,185,129,0.4)", position: "relative" },
    badge: { position: "absolute", top: -6, right: -6, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 800, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" },
    bottomBar: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      background: "rgba(255, 255, 255, 0.92)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderTop: "1px solid rgba(0, 0, 0, 0.08)",
      padding: "12px 16px max(14px, env(safe-area-inset-bottom))",
      boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.06)",
      zIndex: 90,
      display: "flex",
      justifyContent: "center",
    },
    bottomActionBtn: {
      width: "100%",
      maxWidth: 480,
      height: 48,
      borderRadius: 14,
      border: "none",
      background: "linear-gradient(135deg, #7c3aed, #8b5cf6)",
      color: "#fff",
      fontWeight: 700,
      fontSize: 15,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      boxShadow: "0 4px 16px rgba(124, 58, 237, 0.35)",
    },
    hero: { margin: "18px 16px 0", borderRadius: 20, padding: "20px 20px 22px", background: "linear-gradient(135deg,#7c3aed 0%,#a78bfa 60%,#c4b5fd 100%)", boxShadow: "0 8px 32px rgba(124,58,237,0.4)", position: "relative", overflow: "hidden" },
    heroTitle: { color: "#fff", fontSize: 15, fontWeight: 600, margin: "0 0 4px", opacity: 0.9 },
    heroSub: { color: "rgba(255,255,255,0.75)", fontSize: 12.5, margin: 0 },
    statsRow: { display: "flex", gap: 8, margin: "14px 16px 0", overflowX: "auto", paddingBottom: 6, WebkitOverflowScrolling: "touch" },
    statCard: { minWidth: 68, flex: 1, borderRadius: 14, padding: "10px 6px", background: "#fff", border: "1px solid #e5e7eb", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", flexShrink: 0 },
    statNum: { fontSize: 18, fontWeight: 800, margin: "0 0 2px" },
    statLabel: { fontSize: 10.5, color: "#6b7280", margin: 0, fontWeight: 600, whiteSpace: "nowrap" },
    sectionTitle: { color: "#6b7280", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", padding: "20px 16px 8px", margin: 0 },
    /* Order cards */
    orderCard: { margin: "0 16px 10px", borderRadius: 16, padding: "16px", background: "#fff", border: "1px solid #e5e7eb", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" },
    orderTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
    orderNo: { color: "#7c3aed", fontSize: 12, fontWeight: 700, letterSpacing: "0.05em" },
    orderMeta: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    orderDate: { color: "#9ca3af", fontSize: 11.5 },
    badge2: (cfg) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color }),
    /* Search */
    searchWrap: { margin: "12px 16px 6px", position: "relative" },
    searchInput: { width: "100%", padding: "11px 14px 11px 40px", borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff", fontSize: 13.5, outline: "none", boxSizing: "border-box", color: "#111827" },
    /* Category pills */
    pillWrap: { display: "flex", gap: 8, padding: "0 16px 6px", overflowX: "auto", scrollbarWidth: "none" },
    pill: (active) => ({ padding: "6px 14px", borderRadius: 20, border: active ? "none" : "1.5px solid #e5e7eb", background: active ? "#7c3aed" : "#fff", color: active ? "#fff" : "#374151", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }),
    /* 2-col grid */
    grid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, padding: "6px 16px 16px" },
    productCard: { borderRadius: 16, padding: "12px", background: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column" },
    productIcon: { fontSize: 30, marginBottom: 6, textAlign: "center" },
    productName: { fontSize: 12.5, fontWeight: 700, color: "#111827", margin: "0 0 4px", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: 34 },
    productKet: { fontSize: 11, color: "#6b7280", margin: "0 0 6px", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: 28 },
    productMeta: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", marginBottom: 10, fontSize: 11 },
    stokBadge: (stok) => ({ padding: "2px 7px", borderRadius: 6, fontSize: 10.5, fontWeight: 700, background: Number(stok) > 0 ? "#ecfdf5" : "#fef2f2", color: Number(stok) > 0 ? "#059669" : "#dc2626" }),
    productSat: { fontSize: 11, color: "#4b5563", fontWeight: 600 },
    addBtn: { width: "100%", padding: "7px 0", borderRadius: 10, border: "1.5px solid #7c3aed", background: "#fff", color: "#7c3aed", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 },
    qtyControl: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "#F5F3FF", borderRadius: 10, border: "1.5px solid #7c3aed", padding: "2px 4px" },
    stepBtn: { width: 26, height: 26, borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
    qtyNum: { fontSize: 13, fontWeight: 800, color: "#7c3aed", flex: 1, textAlign: "center" },
    /* Floating bar */
    floatingBar: { position: "fixed", bottom: 16, left: 16, right: 16, maxWidth: 480, margin: "0 auto", background: "#1f2937", color: "#fff", borderRadius: 18, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 90, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" },
    checkoutBtn: { display: "flex", alignItems: "center", gap: 6, background: "#7c3aed", color: "#fff", border: "none", borderRadius: 12, padding: "9px 16px", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
    /* Modal */
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", alignItems: "flex-end" },
    sheet: { width: "100%", maxHeight: "92vh", overflowY: "auto", background: "#fff", borderRadius: "24px 24px 0 0", padding: "20px 20px 40px" },
    modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
    modalTitle: { fontSize: 17, fontWeight: 700, color: "#111827", margin: 0 },
    closeBtn: { width: 34, height: 34, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
    /* Form fields */
    label: { fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "11px 14px", borderRadius: 12, border: "1.5px solid #e5e7eb", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", background: "#f9fafb" },
    textarea: { width: "100%", padding: "11px 14px", borderRadius: 12, border: "1.5px solid #e5e7eb", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", background: "#f9fafb", resize: "vertical", minHeight: 72, fontFamily: "inherit" },
    submitBtn: { width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#7c3aed,#a78bfa)", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 8, boxShadow: "0 4px 16px rgba(124,58,237,0.4)" },
    /* Cart items */
    cartItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #f3f4f6" },
    cartItemIcon: { fontSize: 24, flexShrink: 0 },
    cartItemInfo: { flex: 1 },
    /* Detail */
    detailRow: { display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f3f4f6" },
    detailKey: { color: "#6b7280", fontSize: 13 },
    detailVal: { color: "#111827", fontSize: 13, fontWeight: 600, textAlign: "right", maxWidth: "60%" },
    /* Empty */
    emptyWrap: { textAlign: "center", padding: "60px 20px" },
    emptyIcon: { fontSize: 48, marginBottom: 12 },
    emptyTitle: { color: "#111827", fontSize: 16, fontWeight: 600, margin: "0 0 6px" },
    emptyDesc: { color: "#9ca3af", fontSize: 13, margin: 0 },
  };

  /* ═══════════ RENDER ═══════════ */
  if (isFemaleUser) {
    return null;
  }

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ─── HEADER ─── */}
      <div style={s.header}>
        <motion.button style={s.backBtn} onClick={() => navigate(-1)} whileTap={{ scale: 0.9 }} aria-label="Kembali" id="btn-back-atk">
          <ArrowLeft size={22} color="#374151" />
        </motion.button>

        <h1 style={s.title}>Pemesanan ATK</h1>

        {view === "catalog" && (
          <motion.button style={s.cartBtn} onClick={() => cartList.length > 0 && setShowCheckout(true)} whileTap={{ scale: 0.95 }} id="btn-open-cart">
            {cartList.length > 0 && <span style={s.badge}>{cartList.length}</span>}
            <ShoppingCart size={16} /> Keranjang
          </motion.button>
        )}
      </div>

      {/* ─── HERO ─── */}
      <motion.div style={s.hero} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
        <Store size={28} color="rgba(255,255,255,0.65)" style={{ marginBottom: 8 }} />
        <p style={s.heroTitle}>Pemesanan ATK Online</p>
        <p style={s.heroSub}>Pesan alat tulis kantor langsung dari genggaman tanganmu</p>
      </motion.div>

      {/* ─── STATS ─── */}
      <div style={s.statsRow}>
        {[
          { label: "Total", count: orders.length, color: "#7c3aed" },
          { label: "Ngantri", count: orders.filter((o) => {
            const s = String(o.status || "").toLowerCase().trim();
            return ["pending", "baru", "order", "menunggu"].includes(s) || (!s.includes("siap") && !s.includes("kirim") && !s.includes("erima") && !s.includes("terima") && !s.includes("proses") && !s.includes("tolak"));
          }).length, color: "#d97706" },
          { label: "Disiapkan", count: orders.filter((o) => {
            const s = String(o.status || "").toLowerCase().trim();
            return s.includes("siap") || ["approved", "selesai"].includes(s);
          }).length, color: "#059669" },
          { label: "Dikirim", count: orders.filter((o) => String(o.status || "").toLowerCase().includes("kirim")).length, color: "#7c3aed" },
          { label: "Diterima", count: orders.filter((o) => {
            const s = String(o.status || "").toLowerCase().trim();
            return s.includes("erima") || s.includes("terima");
          }).length, color: "#0891b2" },
        ].map((st, i) => (
          <motion.div key={st.label} style={s.statCard} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
            <p style={{ ...s.statNum, color: st.color }}>{st.count}</p>
            <p style={s.statLabel}>{st.label}</p>
          </motion.div>
        ))}
      </div>

      {/* ─── TAB NAVIGATION ─── */}
      <div style={{ display: "flex", margin: "16px 16px 0", borderRadius: 14, overflow: "hidden", border: "1px solid #e5e7eb", background: "#f9fafb" }}>
        {[
          { key: "history", label: "Riwayat", icon: <ListOrdered size={15} /> },
          { key: "catalog", label: "Katalog ATK", icon: <Package size={15} /> },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            style={{ flex: 1, padding: "10px 8px", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13, fontWeight: 700, borderRadius: 13, transition: "all 0.2s", background: view === tab.key ? "#7c3aed" : "transparent", color: view === tab.key ? "#fff" : "#6b7280" }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════ */}
      {/* TAB: RIWAYAT                              */}
      {/* ══════════════════════════════════════════ */}
      {view === "history" && (
        <>
          <p style={s.sectionTitle}>Riwayat Pesanan</p>

          {/* Hist search */}
          <div style={s.searchWrap}>
            <Search size={16} color="#9ca3af" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
            <input type="text" placeholder="Cari pesanan..." value={histSearch} onChange={(e) => setHistSearch(e.target.value)} style={s.searchInput} />
          </div>

          {filteredOrders.length === 0 ? (
            <div style={s.emptyWrap}>
              <div style={s.emptyIcon}>📋</div>
              <p style={s.emptyTitle}>Belum ada pesanan</p>
              <p style={s.emptyDesc}>Tekan "Buat" untuk memulai pemesanan ATK</p>
            </div>
          ) : (
            filteredOrders.map((order, i) => {
              const cfg = getCfg(order.status);
              const canDelete = isDeletableStatus(order.status);
              return (
                <motion.div key={order.id} style={s.orderCard} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} onClick={() => setSelectedOrder(order)} whileTap={{ scale: 0.98 }} id={`order-${order.id}`}>
                  <div style={s.orderTop}>
                    <span style={s.orderNo}>{order.no_order || order.id}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={s.badge2(cfg)}>{cfg.icon} {cfg.label}</span>
                      {String(order.status || "").toLowerCase().includes("kirim") && (
                        <button
                          onClick={(e) => handleConfirmReceived(order, e)}
                          title="Konfirmasi Pesanan Diterima"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 20,
                            border: "none",
                            background: "#ecfdf5",
                            color: "#059669",
                            fontSize: 11,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            cursor: "pointer",
                            boxShadow: "0 1px 3px rgba(5, 150, 105, 0.2)",
                          }}
                        >
                          <CheckCircle size={12} /> Terima
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => handleDeleteOrder(order, e)}
                          title="Hapus / Batalkan Pesanan"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            border: "none",
                            background: "#fee2e2",
                            color: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Headline: keperluan saat antri, catatan saat disiapkan, catatan_kirim saat dikirim, oleh {nama agen} saat diterima */}
                  <p style={{ color: "#111827", fontSize: 14, fontWeight: 600, margin: "2px 0 4px" }}>
                    {(() => {
                      const s = String(order.status || "").toLowerCase().trim();
                      const isTerima = s.includes("erima") || s.includes("terima");
                      const isKirim = s.includes("kirim");
                      const isSiap = s.includes("siap") || ["approved", "selesai"].includes(s);

                      if (isTerima) {
                        const rawAgen = order.pemohon || order.agen || currentUser.nama || "Agen";
                        const cleanAgen = String(rawAgen).replace(/^oleh\s+/i, "").trim();
                        return `Oleh ${cleanAgen}`;
                      }
                      if (isKirim) {
                        return order.catatan_kirim || order.catatan || order.keperluan || "-";
                      }
                      if (isSiap) {
                        return order.catatan || order.keperluan || "-";
                      }
                      return order.keperluan || order.instruksi || "-";
                    })()}
                  </p>

                  {/* Subtitle: jumlah item dan 1 perwakilan nama item */}
                  <p style={{ color: "#6b7280", fontSize: 12.5, margin: "0 0 10px" }}>
                    {(() => {
                      const itemCount = (order.items || []).length;
                      if (itemCount === 0) return "0 item";
                      const first = order.items[0];
                      const firstNama = first.nama || "Item ATK";
                      const firstQty = first.qty ? ` (${first.qty})` : "";
                      const extra = itemCount > 1 ? ` (+${itemCount - 1} lainnya)` : "";
                      return `${itemCount} item · ${firstNama}${firstQty}${extra}`;
                    })()}
                  </p>
                  <div style={s.orderMeta}>
                    <span style={s.orderDate}><MapPin size={11} style={{ verticalAlign: "middle" }} /> {order.lokasi || "-"}</span>
                    <span style={s.orderDate}>{fmtDate(order.tanggal || order.created_at)}</span>
                  </div>
                </motion.div>
              );
            })
          )}
        </>
      )}

      {/* ══════════════════════════════════════════ */}
      {/* TAB: KATALOG (2-col Tokopedia style)       */}
      {/* ══════════════════════════════════════════ */}
      {view === "catalog" && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <p style={{ ...s.sectionTitle, margin: 0 }}>Katalog ATK</p>

            {currentUser.isAgen && (
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#0284c7",
                background: "#e0f2fe",
                border: "1px solid #bae6fd",
                padding: "3px 9px",
                borderRadius: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}>
                <Store size={12} /> Khusus Agen (Untuk: Semua)
              </span>
            )}
          </div>


          {/* Search */}
          <div style={s.searchWrap}>
            <Search size={16} color="#9ca3af" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
            <input type="text" placeholder="Cari ATK, kode, atau keterangan..." value={search} onChange={(e) => setSearch(e.target.value)} style={s.searchInput} />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", cursor: "pointer", padding: 0 }}>
                <X size={16} color="#9ca3af" />
              </button>
            )}
          </div>

          {/* Category pills */}
          <div style={s.pillWrap}>
            {categories.map((cat) => (
              <button key={cat} style={s.pill(catFilter === cat)} onClick={() => setCatFilter(cat)}>{cat}</button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <RefreshCw size={28} color="#7c3aed" style={{ animation: "spin 1s linear infinite" }} />
            </div>
          ) : filteredAssets.length === 0 ? (
            <div style={s.emptyWrap}>
              <div style={s.emptyIcon}>🔍</div>
              <p style={s.emptyTitle}>Tidak ditemukan</p>
              <p style={s.emptyDesc}>Coba kata kunci lain</p>
            </div>
          ) : (
            <div style={s.grid}>
              {filteredAssets.map((asset) => {
                const key = String(asset.id || asset.kode);
                const inCart = cart[key];
                const stokVal = asset.stok_total !== undefined ? asset.stok_total : (asset.stok ?? 0);
                return (
                  <div
                    key={key}
                    id={`product-${key}`}
                    style={{
                      borderRadius: 22,
                      background: "linear-gradient(180deg, #2E3B32 0%, #1E2821 55%, #141C16 100%)",
                      color: "#fff",
                      padding: "9px 9px 12px",
                      boxShadow: "0 10px 24px -4px rgba(0, 0, 0, 0.28), 0 4px 10px -2px rgba(0, 0, 0, 0.15)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Top Media / Banner with subtle gradient and carousel dots */}
                    <div
                      style={{
                        height: 105,
                        borderRadius: 16,
                        background: "linear-gradient(135deg, rgba(62, 82, 68, 0.8) 0%, rgba(26, 36, 29, 0.95) 100%)",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 9,
                        overflow: "hidden",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {/* Ambient light overlay */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: "radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 70%), linear-gradient(180deg, transparent 40%, rgba(20,28,22,0.9) 100%)",
                          pointerEvents: "none",
                        }}
                      />

                      {/* Icon */}
                      <span style={{ fontSize: 36, zIndex: 1, filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.4))" }}>
                        {itemIcon(asset.subkategori || asset.kategori, asset.nama)}
                      </span>

                      {/* Carousel Indicator Dots */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: 6,
                          left: "50%",
                          transform: "translateX(-50%)",
                          display: "flex",
                          gap: 3.5,
                          zIndex: 2,
                        }}
                      >
                        <span style={{ width: 9, height: 3, borderRadius: 2, background: "#ffffff" }} />
                        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(255,255,255,0.35)" }} />
                        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(255,255,255,0.35)" }} />
                      </div>
                    </div>

                    {/* Title and Stok Badge */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 6,
                        marginBottom: 4,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#ffffff",
                          lineHeight: 1.28,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          letterSpacing: "-0.01em",
                          flex: 1,
                        }}
                        title={asset.nama}
                      >
                        {asset.nama || "-"}
                      </p>
                      <span
                        style={{
                          background: Number(stokVal) > 0 ? "rgba(0, 0, 0, 0.45)" : "rgba(220, 38, 38, 0.35)",
                          color: Number(stokVal) > 0 ? "#ffffff" : "#fca5a5",
                          border: "1px solid rgba(255, 255, 255, 0.12)",
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: 10,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {Number(stokVal) > 0 ? `Stok ${stokVal}` : "Habis"}
                      </span>
                    </div>

                    {/* Description / Keterangan */}
                    <p
                      style={{
                        margin: "0 0 8px",
                        fontSize: 10.5,
                        color: "rgba(255, 255, 255, 0.58)",
                        lineHeight: 1.32,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 27,
                      }}
                      title={asset.keterangan || ""}
                    >
                      {asset.keterangan ? asset.keterangan : "Alat tulis & perlengkapan operasional kantor."}
                    </p>

                    {/* Tag Chips (Pills) */}
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 11, marginTop: "auto" }}>
                      <span
                        style={{
                          background: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(4px)",
                          color: "rgba(255, 255, 255, 0.85)",
                          fontSize: 9.5,
                          fontWeight: 600,
                          padding: "2.5px 7px",
                          borderRadius: 20,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          textTransform: "capitalize",
                        }}
                      >
                        {asset.subkategori ? asset.subkategori : (asset.kategori || "ATK")}
                      </span>
                      <span
                        style={{
                          background: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(4px)",
                          color: "rgba(255, 255, 255, 0.85)",
                          fontSize: 9.5,
                          fontWeight: 600,
                          padding: "2.5px 7px",
                          borderRadius: 20,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                        }}
                      >
                        {asset.satuan || "Pcs"}
                      </span>
                    </div>

                    {/* Action Button: White High-Contrast Pill / Stepper */}
                    {inCart ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "#ffffff",
                          borderRadius: 999,
                          padding: "2.5px 4px",
                        }}
                      >
                        <button
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            border: "none",
                            background: "#1E2821",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          onClick={() => updateQty(key, -1)}
                        >
                          <Minus size={12} />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={inCart.qty}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.target.select();
                          }}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || v === "0") {
                              setDirectQty(key, 0, asset);
                            } else {
                              setDirectQty(key, v, asset);
                            }
                          }}
                          style={{
                            width: 46,
                            border: "none",
                            background: "transparent",
                            fontSize: 13,
                            fontWeight: 800,
                            color: "#111827",
                            textAlign: "center",
                            outline: "none",
                            padding: 0,
                            margin: "0 2px",
                          }}
                        />
                        <button
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            border: "none",
                            background: "#1E2821",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          onClick={() => updateQty(key, +1)}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        style={{
                          width: "100%",
                          padding: "8px 0",
                          borderRadius: 999,
                          border: "none",
                          background: "#ffffff",
                          color: "#111827",
                          fontWeight: 800,
                          fontSize: 12.5,
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          transition: "transform 0.1s ease",
                        }}
                        onClick={() => addToCart(asset)}
                      >
                        Pesan
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Floating checkout bar when cart has items */}
          {cartList.length > 0 && (
            <div style={s.floatingBar}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(167,139,250,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShoppingCart size={17} color="#a78bfa" />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>{cartList.length} Jenis Barang</p>
                  <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{totalQty} total item siap diajukan</p>
                </div>
              </div>
              <button style={s.checkoutBtn} onClick={() => setShowCheckout(true)} id="btn-checkout">
                Checkout <ChevronRight size={15} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ─── BOTTOM ACTION BAR (RIWAYAT) ─── */}
      {view === "history" && (
        <div style={s.bottomBar}>
          <motion.button
            style={s.bottomActionBtn}
            onClick={() => setView("catalog")}
            whileTap={{ scale: 0.97 }}
            id="btn-new-atk"
          >
            <Plus size={18} strokeWidth={2.5} />
            Buat Pesanan ATK
          </motion.button>
        </div>
      )}

      {/* ══════════════════════════════════════════ */}
      {/* MODAL: CHECKOUT                           */}
      {/* ══════════════════════════════════════════ */}
      <AnimatePresence>
        {showCheckout && (
          <motion.div style={s.overlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={(e) => e.target === e.currentTarget && setShowCheckout(false)}>
            <motion.div style={s.sheet} initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }}>
              <div style={s.modalHeader}>
                <h2 style={s.modalTitle}>🛒 Checkout Pesanan</h2>
                <button style={s.closeBtn} onClick={() => setShowCheckout(false)} aria-label="Tutup checkout"><X size={18} color="#374151" /></button>
              </div>

              {/* Cart items list */}
              <p style={{ ...s.sectionTitle, padding: "0 0 8px" }}>Item yang Dipesan</p>
              {cartList.map((c) => (
                <div key={c.key} style={s.cartItem}>
                  <span style={s.cartItemIcon}>{itemIcon(c.asset.subkategori || c.asset.kategori, c.asset.nama)}</span>
                  <div style={s.cartItemInfo}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{c.asset.nama}</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{c.satuan}</p>
                  </div>
                  
                  {/* Stepper with manual input in checkout */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", background: "#f3f4f6", borderRadius: 8, padding: "2px 4px", border: "1px solid #e5e7eb" }}>
                      <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: "2px 4px", display: "flex", alignItems: "center" }} onClick={() => updateQty(c.key, -1)}>
                        <Minus size={12} color="#374151" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={c.qty}
                        onClick={(e) => e.target.select()}
                        onChange={(e) => setDirectQty(c.key, e.target.value, c.asset)}
                        style={{
                          width: 38,
                          border: "none",
                          background: "transparent",
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: "#111827",
                          textAlign: "center",
                          outline: "none",
                          padding: 0,
                        }}
                      />
                      <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: "2px 4px", display: "flex", alignItems: "center" }} onClick={() => updateQty(c.key, +1)}>
                        <Plus size={12} color="#374151" />
                      </button>
                    </div>
                    <button onClick={() => updateQty(c.key, -c.qty)} style={{ ...s.stepBtn, background: "#fee2e2", borderRadius: 8, width: 28, height: 28, flexShrink: 0 }} title="Hapus">
                      <X size={13} color="#dc2626" />
                    </button>
                  </div>
                </div>
              ))}

              <div style={{ height: 16 }} />

              {/* Pemohon (readonly) */}
              <label style={s.label}><FileText size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />Pemohon</label>
              <input value={currentUser.nama} readOnly style={{ ...s.input, background: "#f3f4f6", color: "#6b7280", marginBottom: 12 }} />

              {/* Lokasi Tujuan (TomSelect sama seperti di menu Asset: Form Pilih Lokasi Baru) */}
              <div style={{ marginBottom: 14 }}>
                <label style={s.label}>
                  <MapPin size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  Pilih Lokasi Tujuan <span style={{ color: "#ef4444" }}>*</span>
                </label>
                {loadingLoc ? (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>Memuat daftar lokasi...</div>
                ) : (
                  <div style={{ width: "100%" }}>
                    <select ref={selectLocationRef} autoComplete="off" defaultValue={lokasiId} style={{ width: "100%" }}>
                      <option value="">Cari dan pilih...</option>
                    </select>
                    <TomSelectField
                      selectRef={selectLocationRef}
                      options={locations}
                      value={lokasiId}
                      onChange={(val) => {
                        setLokasiId(val);
                        const matched = locations.find((l) => String(l.id) === String(val));
                        if (matched) setLokasi(matched.nama);
                      }}
                      placeholder="Cari dan pilih..."
                    />
                  </div>
                )}
              </div>

              {/* Instruksi */}
              <label style={s.label}><ListOrdered size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />Instruksi <span style={{ color: "#ef4444" }}>*</span></label>
              <textarea value={instruksi} onChange={(e) => setInstruksi(e.target.value)} placeholder="Contoh: Mohon dikirimkan segera ke meja frontdesk / operasional..." style={s.textarea} />

              <button style={s.submitBtn} onClick={handleCheckout} id="btn-submit-checkout">
                ✅ Kirim Pesanan
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════ */}
      {/* MODAL: DETAIL ORDER                       */}
      {/* ══════════════════════════════════════════ */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div style={s.overlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={(e) => e.target === e.currentTarget && setSelectedOrder(null)}>
            <motion.div style={s.sheet} initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }}>
              <div style={s.modalHeader}>
                <h2 style={s.modalTitle}>Detail Pesanan</h2>
                <button style={s.closeBtn} onClick={() => setSelectedOrder(null)} aria-label="Tutup detail"><X size={18} color="#374151" /></button>
              </div>

              {/* Status */}
              {(() => { const cfg = getCfg(selectedOrder.status); return (
                <div style={{ marginBottom: 16 }}>
                  <span style={{ ...s.badge2(cfg), fontSize: 13, padding: "5px 14px" }}>{cfg.icon} {cfg.label}</span>
                </div>
              ); })()}

              {(() => {
                const sLower = String(selectedOrder.status || "").toLowerCase();
                const isSiap = sLower.includes("siap") || ["approved", "selesai"].includes(sLower);
                const isKirim = sLower.includes("kirim");
                const isTerima = sLower.includes("erima") || sLower.includes("terima");

                const rows = [
                  ["No. Order", selectedOrder.no_order],
                  ["Tanggal", fmtDate(selectedOrder.tanggal || selectedOrder.created_at)],
                  ["Pemohon", selectedOrder.pemohon],
                  ["Lokasi", selectedOrder.lokasi],
                  ["Instruksi", selectedOrder.instruksi || selectedOrder.keperluan],
                ];

                // Nilai kolom catatan kalo sudah disiapkan (atau dikirim/diterima)
                if ((isSiap || isKirim || isTerima) && selectedOrder.catatan) {
                  rows.push(["Disiapkan", selectedOrder.catatan]);
                }

                // Nilai kolom catatan_kirim kalo sudah dikirim (atau diterima)
                if ((isKirim || isTerima) && selectedOrder.catatan_kirim) {
                  rows.push(["Catatan Kirim", selectedOrder.catatan_kirim]);
                }

                return rows.filter(([, v]) => v).map(([k, v]) => (
                  <div style={s.detailRow} key={k}>
                    <span style={s.detailKey}>{k}</span>
                    <span style={s.detailVal}>{v}</span>
                  </div>
                ));
              })()}

              {/* Items */}
              <p style={{ ...s.sectionTitle, padding: "16px 0 8px" }}>Item Pesanan ({(selectedOrder.items || []).length})</p>
              {(selectedOrder.items || []).map((item) => (
                <div key={item.id} style={s.cartItem}>
                  <span style={s.cartItemIcon}>{itemIcon("", item.nama)}</span>
                  <div style={s.cartItemInfo}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.nama}</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{item.qty} {item.satuan} · {item.kode}</p>
                  </div>
                </div>
              ))}

              {/* Tombol Konfirmasi Diterima jika status sudah dikirim */}
              {String(selectedOrder.status || "").toLowerCase().includes("kirim") && (
                <button
                  onClick={(e) => handleConfirmReceived(selectedOrder, e)}
                  id="btn-confirm-received"
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: 14,
                    border: "none",
                    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                    marginTop: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: "0 4px 14px rgba(5, 150, 105, 0.35)",
                  }}
                >
                  <CheckCircle size={18} /> Konfirmasi Diterima
                </button>
              )}

              {/* Badge info jika pesanan sudah berstatus diterima */}
              {(String(selectedOrder.status || "").toLowerCase().includes("erima") || String(selectedOrder.status || "").toLowerCase().includes("terima")) && (
                <div style={{
                  marginTop: 20,
                  padding: "12px 16px",
                  borderRadius: 12,
                  background: "#ecfeff",
                  border: "1px solid #a5f3fc",
                  color: "#0e7490",
                  fontSize: 13,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}>
                  <CheckCircle size={16} color="#0891b2" /> Pesanan telah dikonfirmasi diterima
                </div>
              )}

              {/* Tombol Hapus / Batalkan jika status baru/pending/belum disiapkan */}
              {isDeletableStatus(selectedOrder.status) && (
                <button
                  onClick={(e) => handleDeleteOrder(selectedOrder, e)}
                  id="btn-delete-order"
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: 14,
                    border: "1.5px solid #fee2e2",
                    background: "#fff1f2",
                    color: "#e11d48",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                    marginTop: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <Trash2 size={16} /> Batalkan & Hapus Pesanan
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}
